// Renders zombies as simple blocky figures (head, torso, arms, legs) and
// animates a walk cycle. Meshes are pooled by mob id and disposed on death.

import * as THREE from 'three';
import { MobManager, Zombie } from '../sim/mobs';

const SKIN = 0x3a7d33;
const SKIN_HEAD = 0x2f6b2a;

interface MobModel {
  group: THREE.Group;
  legL: THREE.Mesh;
  legR: THREE.Mesh;
  armL: THREE.Mesh;
  armR: THREE.Mesh;
  bodyMats: THREE.MeshLambertMaterial[];
}

export class MobRenderer {
  private scene: THREE.Scene;
  private models = new Map<number, MobModel>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  private build(): MobModel {
    const group = new THREE.Group();
    const bodyMats: THREE.MeshLambertMaterial[] = [];

    const part = (w: number, h: number, d: number, color: number): THREE.Mesh => {
      const mat = new THREE.MeshLambertMaterial({ color });
      bodyMats.push(mat);
      return new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    };

    const torso = part(0.5, 0.7, 0.28, SKIN);
    torso.position.y = 1.0;
    const head = part(0.42, 0.42, 0.42, SKIN_HEAD);
    head.position.y = 1.55;

    const legL = part(0.22, 0.7, 0.24, SKIN);
    legL.geometry.translate(0, -0.35, 0); // pivot at hip
    legL.position.set(-0.13, 0.7, 0);
    const legR = part(0.22, 0.7, 0.24, SKIN);
    legR.geometry.translate(0, -0.35, 0);
    legR.position.set(0.13, 0.7, 0);

    const armL = part(0.2, 0.6, 0.2, SKIN);
    armL.geometry.translate(0, -0.3, 0);
    armL.position.set(-0.35, 1.32, 0.28); // arms forward, zombie-style
    const armR = part(0.2, 0.6, 0.2, SKIN);
    armR.geometry.translate(0, -0.3, 0);
    armR.position.set(0.35, 1.32, 0.28);

    group.add(torso, head, legL, legR, armL, armR);
    return { group, legL, legR, armL, armR, bodyMats };
  }

  sync(mobs: MobManager): void {
    const alive = new Set<number>();
    for (const mob of mobs.mobs) {
      alive.add(mob.id);
      let model = this.models.get(mob.id);
      if (!model) {
        model = this.build();
        this.scene.add(model.group);
        this.models.set(mob.id, model);
      }
      this.updateModel(model, mob);
    }
    for (const [id, model] of this.models) {
      if (!alive.has(id)) {
        this.scene.remove(model.group);
        model.group.traverse((o) => {
          if (o instanceof THREE.Mesh) o.geometry.dispose();
        });
        this.models.delete(id);
      }
    }
  }

  private updateModel(model: MobModel, mob: Zombie): void {
    model.group.position.set(mob.pos.x, mob.pos.y, mob.pos.z);
    model.group.rotation.y = mob.yaw;

    const swing = Math.sin(mob.walkPhase) * 0.6;
    model.legL.rotation.x = swing;
    model.legR.rotation.x = -swing;
    model.armL.rotation.x = -0.4 + swing * 0.3;
    model.armR.rotation.x = -0.4 - swing * 0.3;

    // Red flash while hurt.
    const flash = mob.hurtFlash > 0;
    for (const mat of model.bodyMats) {
      mat.emissive.setHex(flash ? 0x661111 : 0x000000);
    }
  }

  clear(): void {
    for (const [, model] of this.models) {
      this.scene.remove(model.group);
    }
    this.models.clear();
  }
}
